import { EMPTY_LIST, EMPTY_OBJECT } from '../../../constants/common'
import { useContentOverflow } from '../../../hooks'
import { Avatar } from '../../Avatar'
import { BulletList } from '../../BulletList'
import { EntityArchivedIcon } from '../../Entity'
import { FloatingTooltip } from '../../Floating'
import Text from '../../Text'

import { SEARCH_ITEM_ICON_SIZE } from './SearchItem.constants'

import * as styles from './SearchItem.module.css'

/**
 * @import {BulletListProps} from "../../BulletList/BulletList"
 */

/**
 * @typedef {object} SearchItemAddititonProps
 *
 * @property {string} [className]
 */

/**
 * @typedef {object} SearchItemIcon
 *
 * @property {string | null} [color]
 *
 * @property {string | null} [src]
 *
 * @property {React.ReactNode} [icon]
 *
 * @property {string | null} [text]
 */

/**
 * @typedef {object} SearchItemIconProps
 *
 * @property {string} [className]
 *
 * @property {number} [size]
 */

/**
 * @typedef {object} SearchItemProps
 *
 * @property {React.ComponentType<BulletListProps>} [Addititonal]
 *
 * @property {string} [Element]
 *
 * @property {React.ComponentType<SearchItemIcon | SearchItemIconProps>} [Icon]
 *
 * @property {React.ReactNode[]} [additional]
 *
 * @property {BulletListProps} [additionalProps]
 *
 * @property {SearchItemIcon | null} [icon]
 *
 * @property {SearchItemIconProps} [iconProps]
 *
 * @property {boolean} [archived]
 *
 * @property {string} [className]
 *
 * @property {React.ReactNode} [status]
 *
 * @property {string} [title]
 */

/**
 * @param {SearchItemProps} props
 */
function SearchItem({
  Element = 'div',
  Icon = Avatar,
  additional = EMPTY_LIST,
  additionalProps: {
    className: additionalClassName = styles.additional,
    ...additionalProps
  } = EMPTY_OBJECT,
  archived,
  className = styles.SearchItem,
  icon = null,
  iconProps: {
    className: iconClassName = styles.icon,
    size: iconSize = SEARCH_ITEM_ICON_SIZE,
    ...iconProps
  } = EMPTY_OBJECT,
  status,
  title,
  ...elementProps
}) {
  const { overflow, setContentOverflowRef } = useContentOverflow()

  return (
    <Element {...elementProps} className={className}>
      {icon ? (
        <Icon
          {...iconProps}
          {...icon}
          className={iconClassName}
          size={iconSize}
        />
      ) : null}
      <div className={styles.info}>
        <div className={styles.header}>
          <FloatingTooltip content={title} disabled={!overflow}>
            <Text className={styles.title} ref={setContentOverflowRef}>
              {title}
            </Text>
          </FloatingTooltip>
          {archived || status ? (
            <div className={styles.status}>
              {status}
              {archived ? <EntityArchivedIcon /> : null}
            </div>
          ) : null}
        </div>
        {additional.length > 0 ? (
          <BulletList
            {...additionalProps}
            className={additionalClassName}
            value={additional}
          />
        ) : null}
      </div>
    </Element>
  )
}

export default SearchItem
